import { defineStore } from 'pinia';

// User preferences that belong to the browser, not to the embedding project
// context (see settings.js for that). Persisted to localStorage so they
// survive reloads; every access is guarded because the app runs inside an
// iframe where storage can be partitioned or blocked entirely.

export const PREFERENCES_STORAGE_KEY = 'rdrive-task-manager.preferences';

const DEFAULT_PREFERENCES = Object.freeze({
  // Gzip large request bodies (schedule commits, task-set deployments).
  // Off by default: the RDrive API only decompresses request bodies on
  // servers that have request decompression deployed — a gzipped body sent
  // to one that doesn't reaches the JSON binder as raw bytes and 400s with
  // "'0x1F' is an invalid start of a value".
  gzipRequests: false,
});

function readStoredPreferences() {
  try {
    const raw = window.localStorage?.getItem(PREFERENCES_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeStoredPreferences(preferences) {
  try {
    window.localStorage?.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(preferences));
  } catch {
    // Storage unavailable (private mode, blocked third-party iframe, quota):
    // the in-memory value still applies for this session.
  }
}

export const usePreferencesStore = defineStore('preferences', {
  state: () => {
    const stored = readStoredPreferences();
    const state = { ...DEFAULT_PREFERENCES };
    // Only known keys with the expected type are taken from storage; a stale
    // or foreign key in the stored blob is ignored rather than leaking in.
    for (const key of Object.keys(DEFAULT_PREFERENCES)) {
      if (typeof stored[key] === typeof DEFAULT_PREFERENCES[key]) state[key] = stored[key];
    }
    return state;
  },
  actions: {
    setGzipRequests(value) {
      this.gzipRequests = Boolean(value);
      this.persist();
    },
    persist() {
      writeStoredPreferences(
        Object.fromEntries(Object.keys(DEFAULT_PREFERENCES).map((key) => [key, this[key]]))
      );
    },
  },
});
